import { Button } from '@/components/ui/button';
import { getImageUrl } from '@/lib/image';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { CheckCheck, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/**
 * Structural minimum, so this card accepts both `@/types/model`'s `Voucher` and the
 * `FeaturedVoucher` shape the home page receives — they disagree on other fields.
 */
export interface VoucherCardVoucher {
  id: number;
  code: string;
  title?: string | null;
  image?: string | null;
}

interface VoucherCardProps {
  voucher: VoucherCardVoucher;
  className?: string;
  featured?: boolean;
  imageSrc?: string;
  /** Turns the card into a link to the voucher's detail page. The copy button stays clickable. */
  href?: string;
}

const VoucherCard = ({ voucher, className, featured, imageSrc, href }: VoucherCardProps) => {
  const resolvedImageSrc = imageSrc ?? (voucher.image ? getImageUrl(voucher.image, 'voucher') : null);
  const [isCopied, setIsCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => void (resetTimer.current && clearTimeout(resetTimer.current)), []);

  const handleCopy = async () => {
    if (!voucher.code) return;
    try {
      await navigator.clipboard.writeText(voucher.code);
      setIsCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (insecure origin, denied permission) — the code
      // stays visible on the card, so the user can still select it manually.
    }
  };

  return (
    // `min-h` guards call sites that only cap height (the admin preview) so the card
    // can never collapse to its content and crop the artwork.
    <div className={cn('group relative min-h-[140px] overflow-hidden rounded-2xl', className)}>
      {resolvedImageSrc ? (
        <>
          <img
            src={resolvedImageSrc}
            alt={voucher.title ?? 'Ưu đãi'}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
        </>
      ) : (
        // No artwork — lead with the code itself rather than a stand-in icon tile.
        <div className="absolute inset-0" style={{ backgroundColor: 'var(--surface-muted)' }} />
      )}

      {/* A sibling overlay rather than a wrapper: nesting the copy <button> inside an
          <a> is invalid, and a tap on the code would navigate instead of copying. */}
      {href && (
        <Link
          href={href}
          aria-label={voucher.title ? `Xem chi tiết ${voucher.title}` : `Xem chi tiết mã ${voucher.code}`}
          className="focus-visible:ring-primary/60 absolute inset-0 z-10 rounded-2xl outline-none focus-visible:ring-[3px]"
        />
      )}

      <div className={cn('pointer-events-none relative z-20 flex h-full flex-col justify-end gap-3 p-4', !resolvedImageSrc && 'justify-between')}>
        {!resolvedImageSrc && (
          <span className="text-primary font-mono text-lg font-bold tracking-wider break-all">{voucher.code}</span>
        )}

        <p
          className={cn(
            'font-bold',
            resolvedImageSrc ? 'text-white drop-shadow-sm' : 'text-gray-900',
            featured ? 'line-clamp-3 text-lg' : 'line-clamp-2 text-base',
          )}
        >
          {voucher.title}
        </p>

        {voucher.code && (
          <Button
            type="button"
            size="sm"
            variant={resolvedImageSrc ? 'default' : 'outline'}
            onClick={handleCopy}
            className="pointer-events-auto w-fit gap-2 rounded-full px-4!"
          >
            <span className="font-mono font-semibold">{voucher.code}</span>
            {isCopied ? <CheckCheck className="size-4" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
          </Button>
        )}
      </div>

      <span aria-live="polite" className="sr-only">
        {isCopied ? `Đã sao chép mã ${voucher.code}` : ''}
      </span>
    </div>
  );
};

export { VoucherCard };
